import { Link } from "react-router-dom";
import { CalendarClock, Smile, CreditCard, ArrowRight, Sparkles } from "lucide-react";

const AQUA = "#5FE3D0";
const NAVY = "#1E3A8A";
const HERO_IMG = "https://images.unsplash.com/photo-1623076189461-f7706b741c04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function CtaButton({ testid }) {
  return (
    <Link
      to="/tutors"
      data-testid={testid}
      className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full px-8 py-4 text-lg font-bold shadow-lg shadow-teal-300/50 transition-transform duration-200 hover:scale-[1.03] active:scale-95"
      style={{ backgroundColor: AQUA, color: NAVY, fontFamily: "Manrope, sans-serif" }}
    >
      Book a Free Trial Class
      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="welcome-hero">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-40 blur-2xl" style={{ backgroundColor: AQUA }} />
      <div className="absolute -bottom-20 -left-16 w-56 h-56 rounded-full bg-amber-200 opacity-50 blur-2xl" />
      <div className="relative max-w-6xl mx-auto px-5 pt-6 pb-12 sm:pb-20 grid lg:grid-cols-2 gap-10 items-center">
        <div className="welcome-rise">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-teal-200 px-3 py-1 text-xs font-semibold text-teal-800 mb-5">
            <Sparkles className="w-3.5 h-3.5" /> Live online English classes
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5" style={{ color: NAVY, fontFamily: "Manrope, sans-serif" }}>
            Welcome to LanguaConnect English Class!
          </h1>
          <p className="text-base sm:text-lg text-slate-700 mb-8 max-w-xl">
            Hello parents and learners! We make learning English fun and effective with live online classes, mainly
            for children, with adults always welcome too. Every lesson is taught by a friendly, qualified tutor.
          </p>
          <CtaButton testid="welcome-cta-hero" />
          <p className="mt-3 text-sm text-slate-500" data-testid="welcome-no-signup-note">Free trial · No account needed to book</p>
        </div>
        <div className="welcome-rise relative" style={{ animationDelay: "120ms" }}>
          <img
            src={HERO_IMG}
            alt="A child enjoying an online English class on a laptop"
            className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-2xl border-4 border-white"
            data-testid="welcome-hero-image"
          />
          <img
            src="/languaconnect-logo.png"
            alt=""
            className="absolute -bottom-6 -left-4 w-24 h-24 sm:w-28 sm:h-28 rounded-full shadow-xl border-4 border-white"
          />
        </div>
      </div>
    </section>
  );
}

const OFFERS = [
  { title: "Fun lessons for kids", text: "Games, stories and songs that keep children excited to speak English." },
  { title: "Adults welcome too", text: "Conversation and everyday English at your own pace." },
  { title: "Live with a real tutor", text: "Face-to-face online classes, never pre-recorded videos." },
];

function Offer() {
  return (
    <section className="max-w-6xl mx-auto px-5 py-12" data-testid="welcome-offer">
      <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-teal-700 mb-6">What we offer</h2>
      <div className="grid sm:grid-cols-3 gap-4">
        {OFFERS.map((o, i) => (
          <div key={o.title} className="welcome-rise rounded-2xl bg-white p-6 border border-slate-100 shadow-sm" style={{ animationDelay: `${i * 90}ms` }}>
            <h3 className="text-lg font-bold mb-1" style={{ color: NAVY, fontFamily: "Manrope, sans-serif" }}>{o.title}</h3>
            <p className="text-sm text-slate-600">{o.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const TRUST = [
  { icon: CalendarClock, title: "Flexible times", text: "Choose class times that fit your family's schedule." },
  { icon: Smile, title: "Friendly qualified tutors", text: "Patient, experienced teachers who love helping learners grow." },
  { icon: CreditCard, title: "Easy online booking & payment", text: "Book in minutes and pay securely online." },
];

function Trust() {
  return (
    <section className="py-12" style={{ backgroundColor: NAVY }} data-testid="welcome-trust">
      <div className="max-w-6xl mx-auto px-5 grid sm:grid-cols-3 gap-8">
        {TRUST.map(({ icon: Icon, title, text }) => (
          <div key={title} className="flex sm:flex-col gap-4">
            <div className="shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: AQUA }}>
              <Icon className="w-6 h-6" style={{ color: NAVY }} />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</h3>
              <p className="text-sm text-blue-100">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Welcome() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-white to-amber-50" data-testid="welcome-page">
      <style>{`@keyframes welcomeRise{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}.welcome-rise{animation:welcomeRise .6s ease-out both}`}</style>
      <header className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-3">
        <img src="/languaconnect-logo.png" alt="LanguaConnect logo" className="w-11 h-11 rounded-full" data-testid="welcome-logo" />
        <span className="font-extrabold text-lg" style={{ color: NAVY, fontFamily: "Manrope, sans-serif" }}>LanguaConnect English Class</span>
      </header>
      <Hero />
      <Offer />
      <Trust />
      <section className="max-w-3xl mx-auto px-5 py-14 text-center" data-testid="welcome-final-cta">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-3" style={{ color: NAVY, fontFamily: "Manrope, sans-serif" }}>Ready to start speaking English?</h2>
        <p className="text-slate-600 mb-7">Meet our tutors and book your free trial class today.</p>
        <CtaButton testid="welcome-cta-bottom" />
      </section>
      <footer className="text-center text-xs text-slate-400 pb-8">© {new Date().getFullYear()} LanguaConnect English Class</footer>
    </div>
  );
}
